import { useMemo } from "react";
import type { FliptClient } from "@flipt-io/flipt-client-js";
import { useFliptSelector } from "@flipt-io/flipt-client-react";
import useUser from "~/contexts/user";
import { FLIPT_ROUTE_KEYS } from "~/router/constants";

export interface ManifoldNextGenFeature {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Flipt `manifold-nextgen`, read at the router to gate `/nextgen` routes.
 * Exposes `isLoading` while Flipt initializes so we do not flash a false `enabled` and redirect.
 */
export const useManifoldNextGenFeature = (): ManifoldNextGenFeature => {
  const user = useUser();

  const isFliptPending = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, _error: Error | null) => !client || isLoading
  );

  const enabled = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, error: Error | null) => {
      if (!client || isLoading) {
        return false;
      }
      if (error) {
        return false;
      }
      try {
        return client.evaluateBoolean({
          flagKey: FLIPT_ROUTE_KEYS.MANIFOLD_NEXTGEN,
          entityId: user?.email ?? "",
          context: {},
        }).enabled;
      } catch (e) {
        console.error(`Error evaluating boolean flag ${FLIPT_ROUTE_KEYS.MANIFOLD_NEXTGEN}:`, e);
        return false;
      }
    }
  );

  return useMemo(() => ({ isLoading: isFliptPending, enabled }), [isFliptPending, enabled]);
};
